import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useStoreWithEqualityFn } from 'zustand/traditional';

import { openReplayStateLogger } from '../../../hooks/open-replay';

export const DEFAULT_TAXONOMY_ID = 0;

type EnrichmentJobTaxonomySettings = {
  taxonomyId: number;
  includeV2: boolean;
};

export type DataEnrichmentStoreState = {
  enrichmentModals: string[];
  enrichmentJobTaxonomySettings: EnrichmentJobTaxonomySettings;
};

export type DataEnrichmentStoreActions = {
  setEnrichmentModals: (value: string[]) => void;
  setEnrichmentJobTaxonomySettings: (
    value: EnrichmentJobTaxonomySettings
  ) => void;
  resetState: () => void;
};

const initialState: DataEnrichmentStoreState = {
  enrichmentModals: [],
  enrichmentJobTaxonomySettings: {
    taxonomyId: DEFAULT_TAXONOMY_ID,
    includeV2: false,
  },
};

const useDataEnrichmentStore = create<
  DataEnrichmentStoreState & { actions: DataEnrichmentStoreActions }
>()(
  devtools(
    openReplayStateLogger(
      (set) => ({
        ...initialState,
        actions: {
          setEnrichmentModals: (value) => set({ enrichmentModals: value }),
          setEnrichmentJobTaxonomySettings: (value) =>
            set({ enrichmentJobTaxonomySettings: value }),
          resetState: () => set(initialState),
        },
      }),
      'data_enrichment'
    ),
    { name: 'DataEnrichment' }
  )
);

export const useDataEnrichmentSelector = <T>(
  selector: (state: DataEnrichmentStoreState) => T
) => useStoreWithEqualityFn(useDataEnrichmentStore, selector);

export const useDataEnrichmentActions = () =>
  useDataEnrichmentStore((s) => s.actions);

export const selectEnrichmentModals = (s: DataEnrichmentStoreState) =>
  s.enrichmentModals;
export const selectEnrichmentJobTaxonomySettings = (
  s: DataEnrichmentStoreState
) => s.enrichmentJobTaxonomySettings;

export const resetDataEnrichmentStore = () =>
  useDataEnrichmentStore.getState().actions.resetState();
